import { type FC } from 'react'

import { styled } from 'styled-components'

import { Select, TextInput, Checkbox } from 'core/components'
import Clickable from 'core/components/lib/Clickable'
import { buildSelectOptions } from 'core/components/lib/DynamicForm/logic/fields'
import type { StaticFieldSchema } from 'core/utils/generated/formSchemas'

/**
 * `readOnly` is emitted only into the schemas that carry one, so the shared
 * generated type does not declare it.
 */
type ItemFieldSchema = StaticFieldSchema & {
  readOnly?: boolean
  nested?: Array<ItemFieldSchema>
}

type ArrayEditorProps = {
  name?: string
  value: Array<any>
  onChange: (newValue: Array<any>) => void
  itemSchema: Array<ItemFieldSchema>
  readOnly?: boolean
}

/**
 * An array of primitives, e.g. `roleIds`. The generator describes its item as
 * one synthetic field keyed `0`, which is a position rather than a property:
 * the item *is* the value. Rendering it as a one-field object reads `'ROLE-1'[0]`
 * and shows `R`, and writing back spreads the string into `{0:'R',1:'O',...}`.
 */
const scalarItemField = (
  itemSchema: Array<StaticFieldSchema>,
): StaticFieldSchema | undefined =>
  itemSchema.length === 1 &&
  itemSchema[0]?.key === '0' &&
  itemSchema[0]?.type !== 'object' &&
  itemSchema[0]?.type !== 'array'
    ? itemSchema[0]
    : undefined

const ArrayEditor: FC<ArrayEditorProps> = ({
  value = [],
  onChange,
  itemSchema,
  readOnly,
}) => {
  const scalarField = scalarItemField(itemSchema)

  const addItem = () => {
    const newItem = scalarField
      ? emptyValueForType(scalarField.type)
      : createEmptyItem(itemSchema)
    onChange([...value, newItem])
  }

  const removeItem = (index: number) => {
    const newValue = value.filter((_, i) => i !== index)
    onChange(newValue)
  }

  const updateItem = (index: number, newItemValue: any) => {
    const newValue = [...value]
    newValue[index] = newItemValue
    onChange(newValue)
  }

  return (
    <ArrayContainer>
      <ArrayItems>
        {value.map((item, index) => (
          <ArrayItem key={`item-${index}`}>
            <ItemHeader>
              <ItemLabel>Item {index + 1}</ItemLabel>
              {readOnly ? null : (
                <RemoveButton
                  onClick={() => removeItem(index)}
                  size='sm'
                  type='button'
                  variant='secondary'
                >
                  Remove
                </RemoveButton>
              )}
            </ItemHeader>
            <ItemContent>
              {scalarField ? (
                <ItemFields>
                  <ItemField>
                    <FieldInput>
                      <SimpleFieldRenderer
                        field={scalarField}
                        onChange={(newValue) => updateItem(index, newValue)}
                        readOnly={!!readOnly}
                        value={item}
                      />
                    </FieldInput>
                  </ItemField>
                </ItemFields>
              ) : (
                <MiniDynamicForm
                  item={item}
                  onUpdate={(newItem) => updateItem(index, newItem)}
                  readOnly={!!readOnly}
                  schema={itemSchema}
                />
              )}
            </ItemContent>
          </ArrayItem>
        ))}
      </ArrayItems>
      {readOnly ? null : (
        <AddButtonContainer>
          <AddButton>
            <Clickable
              onClick={addItem}
              size='sm'
              type='button'
              variant='outline'
            >
              + Add Item
            </Clickable>
          </AddButton>
        </AddButtonContainer>
      )}
    </ArrayContainer>
  )
}

// Helper function to create empty item from schema
const emptyValueForType = (type: StaticFieldSchema['type']): any => {
  switch (type) {
    case 'string':
    case 'enum':
      return ''
    case 'number':
      return 0
    case 'boolean':
      return false
    case 'array':
      return []
    default:
      return null
  }
}

const createEmptyItem = (schema: Array<ItemFieldSchema>): any => {
  const item: any = {}

  schema.forEach((field) => {
    // A new item has no omit path derived from the record, so seeding a
    // read-only key here is what sends it on the next update.
    if (field.readOnly === true) return

    switch (field.type) {
      case 'string':
        item[field.key] = ''
        break
      case 'number':
        item[field.key] = 0
        break
      case 'boolean':
        item[field.key] = false
        break
      case 'array':
        item[field.key] = []
        break
      case 'object':
        item[field.key] = field.nested ? createEmptyItem(field.nested) : {}
        break
      default:
        item[field.key] = null
    }
  })

  return item
}

// Mini form renderer for array items
const MiniDynamicForm: FC<{
  item: any
  schema: Array<ItemFieldSchema>
  onUpdate: (newItem: any) => void
  readOnly: boolean
}> = ({ item, schema, onUpdate, readOnly }) => {
  const handleFieldChange = (fieldKey: string, newValue: any) => {
    const updatedItem = {
      ...item,
      [fieldKey]: newValue,
    }
    onUpdate(updatedItem)
  }

  return (
    <ItemFields>
      {schema.map((field) => (
        <ItemField key={field.key}>
          <FieldLabel>{field.key}</FieldLabel>
          <FieldInput>
            <SimpleFieldRenderer
              field={field}
              onChange={(newValue) => handleFieldChange(field.key, newValue)}
              // The spec's own statement about the field, which the
              // array-wide flag knows nothing about.
              readOnly={readOnly || field.readOnly === true}
              value={item[field.key]}
            />
          </FieldInput>
        </ItemField>
      ))}
    </ItemFields>
  )
}

// Simple field renderer for array item fields
const SimpleFieldRenderer: FC<{
  field: StaticFieldSchema
  value: any
  onChange: (value: any) => void
  onBlur?: () => void
  readOnly?: boolean
}> = ({ field, value, onChange, readOnly }) => {
  if (readOnly) {
    const display =
      value === null
        ? 'null'
        : typeof value === 'string'
        ? value === ''
          ? '""'
          : value
        : typeof value === 'boolean'
        ? value
          ? 'true'
          : 'false'
        : value === undefined
        ? ''
        : String(value)

    return <MonospaceValue>{display}</MonospaceValue>
  }
  // Render using core components to match DynamicForm visuals
  if (field.type === 'boolean') {
    return (
      <Checkbox
        name={field.key}
        onChange={(v: any) => onChange(!!v)}
        value={!!value as any}
      />
    )
  }

  if (field.type === 'number') {
    return (
      <TextInput
        name={field.key}
        onChange={(v: any) => {
          const str = String(v ?? '')
          const num = str.trim() === '' ? 0 : Number(str)
          onChange(Number.isNaN(num) ? 0 : num)
        }}
        value={value ?? ''}
        variant='outline'
        width='100%'
      />
    )
  }

  if (field.options && Array.isArray(field.options)) {
    const options = buildSelectOptions(field.options as Array<string>)
    return (
      <Select
        name={field.key}
        onChange={(val: any) => onChange(val)}
        options={options}
        value={(value ?? null) as any}
        width='100%'
      />
    )
  }

  return (
    <TextInput
      name={field.key}
      onChange={(v: any) => onChange(v)}
      value={value ?? ''}
      variant='outline'
      width='100%'
    />
  )
}

export default ArrayEditor

// Styled components
const ArrayContainer = styled.div`
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 4px;
  background-color: ${(p) => p.theme.background};
  padding: 12px;
`

const AddButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`

const ArrayItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
`

const ArrayItem = styled.div`
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 4px;
  background-color: ${(p) => p.theme.offset};
`

const ItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${(p) => p.theme.border};
  background-color: ${(p) => p.theme.background};
  padding: 8px 12px;
`

const ItemLabel = styled.div`
  color: ${(p) => p.theme.text};
  font-size: 14px;
  font-weight: 500;
`

const RemoveButton = styled(Clickable)`
  margin-left: auto;
`

const ItemContent = styled.div`
  padding: 12px;
`

const ItemFields = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 2px;
  background-color: ${(p) => p.theme.background};
`

const ItemField = styled.div`
  display: contents;
`

const FieldLabel = styled.div`
  border-right: 1px solid ${(p) => p.theme.border};
  padding: 8px;
  min-width: 140px;
  color: ${(p) => p.theme.text};
  font-size: 12px;
  font-weight: 500;
`

const FieldInput = styled.div`
  padding: 8px;
`

const AddButton = styled.div`
  display: flex;
  justify-content: flex-start;
`

const MonospaceValue = styled.div`
  color: ${(p) => p.theme.text};
  font-family: Inter, Heebo, sans-serif;
  font-size: 12px;
`
